<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Stock Transfers</h1>
          <p class="lede">Move stock between shops. Two-step: sender ships, receiver counts and confirms.</p>
        </div>
        <button class="primary" @click="openNew" v-if="canTransfer && shops.length > 1">+ New transfer</button>
      </div>

      <p v-if="shops.length <= 1" class="muted center" style="padding:30px 0;">
        You need at least two shops to use transfers. Add a second shop in <router-link to="/settings">Settings</router-link>.
      </p>

      <div class="card" v-else>
        <table v-if="rows.length">
          <thead><tr><th>#</th><th>From</th><th>To</th><th>Status</th><th class="r">Sent</th><th class="r">Received</th><th>Created</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in rows" :key="r.id" @click="openDetail(r.id)" class="clickable">
              <td><strong>#{{ r.id }}</strong></td>
              <td>{{ r.from_shop_name }}</td>
              <td>{{ r.to_shop_name }}</td>
              <td><span :class="'st-pill ' + r.status">{{ r.status.replace(/_/g,' ') }}</span></td>
              <td class="r">{{ Math.round(r.total_sent || 0) }}</td>
              <td class="r">{{ Math.round(r.total_received || 0) }}</td>
              <td class="muted small">{{ r.created_at }}</td>
              <td><button class="secondary small">Open</button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No transfers yet.</p>
      </div>
    </div>

    <!-- New / Edit modal -->
    <div v-if="showForm" class="modal-back" @click.self="showForm=false">
      <div class="modal card wide">
        <h3>{{ form.id ? 'Edit transfer' : 'New transfer' }}</h3>
        <div class="row-2">
          <div>
            <label>From shop</label>
            <select v-model.number="form.fromShopId" @change="loadFromShopStock">
              <option :value="0" disabled>-- source --</option>
              <option v-for="s in shops" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div>
            <label>To shop</label>
            <select v-model.number="form.toShopId">
              <option :value="0" disabled>-- destination --</option>
              <option v-for="s in shops" :key="s.id" :value="s.id" :disabled="s.id === form.fromShopId">{{ s.name }}</option>
            </select>
          </div>
        </div>

        <label>Items to transfer</label>
        <input v-model="varSearch" @input="onVarSearch" placeholder="Search variant at source shop…" />
        <div v-if="varResults.length" class="var-results">
          <div v-for="v in varResults" :key="v.variant_id" class="var-result" @click="addLine(v)">
            <strong>{{ v.product_name }}</strong> · {{ v.variant_name }}
            <span class="muted small">· {{ v.sku }} · On hand: {{ v.on_hand }}</span>
          </div>
        </div>

        <table class="items-mini" v-if="form.items.length">
          <thead><tr><th>Item</th><th class="r">On hand</th><th style="width:80px">Send qty</th><th></th></tr></thead>
          <tbody>
            <tr v-for="(it, i) in form.items" :key="i">
              <td>{{ it.product_name }} · {{ it.variant_name }}<div class="muted small">{{ it.sku }}</div></td>
              <td class="r">{{ it.on_hand }}</td>
              <td><input type="number" min="1" :max="it.on_hand" v-model.number="it.sentQty" style="width:70px; padding:4px" /></td>
              <td><button class="danger small" @click="form.items.splice(i,1)">&times;</button></td>
            </tr>
          </tbody>
        </table>

        <label>Notes</label>
        <input v-model="form.notes" placeholder="Optional" />
        <div class="modal-actions">
          <button class="secondary" @click="showForm=false">Cancel</button>
          <button class="primary" @click="save" :disabled="saving || !canSave">{{ saving ? 'Saving…' : 'Save as draft' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>

    <!-- Detail modal -->
    <div v-if="detail" class="modal-back" @click.self="detail=null">
      <div class="modal card wide">
        <div class="detail-head">
          <div>
            <h3>Transfer #{{ detail.id }}</h3>
            <p class="muted">
              {{ detail.from_shop_name }} → {{ detail.to_shop_name }} ·
              <span :class="'st-pill ' + detail.status">{{ detail.status.replace(/_/g,' ') }}</span>
            </p>
          </div>
          <button class="secondary" @click="detail=null">Close</button>
        </div>

        <table>
          <thead><tr><th>Item</th><th class="r">Sent</th><th class="r">Received</th><th class="r" v-if="canReceive">Enter qty received</th></tr></thead>
          <tbody>
            <tr v-for="it in detail.items" :key="it.id">
              <td>{{ it.product_name }} · {{ it.variant_name }}<div class="muted small">{{ it.sku }}</div></td>
              <td class="r"><strong>{{ Math.round(it.sent_qty) }}</strong></td>
              <td class="r">{{ it.received_qty !== null ? Math.round(it.received_qty) : '—' }}</td>
              <td class="r" v-if="canReceive">
                <input type="number" min="0" :max="it.sent_qty" v-model.number="receipts[it.id]" style="width:70px; padding:4px; text-align:right" />
              </td>
            </tr>
          </tbody>
        </table>

        <div class="detail-actions">
          <button v-if="detail.status === 'draft'" class="secondary" @click="sendTransfer">Send (ship stock)</button>
          <button v-if="detail.status === 'draft'" class="danger" @click="cancelTransfer">Cancel draft</button>
          <button v-if="canReceive" class="primary" @click="receiveTransfer" :disabled="receiving">
            {{ receiving ? 'Receiving…' : 'Confirm receipt' }}
          </button>
        </div>
        <p class="err" v-if="detailErr">{{ detailErr }}</p>
        <p class="ok"  v-if="detailOk">{{ detailOk }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute, can } from '@/api';

let varTimer = null;

export default {
  name: 'StockTransfers',
  components: { Navigation },
  data() {
    return {
      rows: [], shops: [], sourceStock: [],
      showForm: false, saving: false, err: '',
      form: { id: 0, fromShopId: 0, toShopId: 0, notes: '', items: [] },
      varSearch: '', varResults: [],
      detail: null, detailErr: '', detailOk: '', receipts: {}, receiving: false,
    };
  },
  computed: {
    canTransfer() { return can('stock.transfer') || sessionStorage.getItem('userRole') === 'owner'; },
    canSave() { return this.form.fromShopId && this.form.toShopId && this.form.fromShopId !== this.form.toShopId && this.form.items.length > 0; },
    canReceive() { return this.detail && this.detail.status === 'in_transit'; },
  },
  async mounted() { await Promise.all([this.load(), this.loadShops()]); },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE'); },
    async load() {
      const { data } = await execute({ function: 'listStockTransfers' }, paysol);
      if (data.success) this.rows = data.data;
    },
    async loadShops() {
      const { data } = await execute({ function: 'listShops' }, paysol);
      if (data.success) this.shops = data.data.filter(s => s.is_active);
    },
    openNew() {
      const defaultShop = parseInt(sessionStorage.getItem('defaultShopId') || '0', 10);
      this.form = { id: 0, fromShopId: defaultShop, toShopId: 0, notes: '', items: [] };
      this.varSearch = ''; this.varResults = []; this.sourceStock = []; this.err = '';
      this.showForm = true;
      if (defaultShop) this.loadFromShopStock();
    },
    async loadFromShopStock() {
      if (!this.form.fromShopId) return;
      const { data } = await execute({ function: 'getStockLevels', shopId: this.form.fromShopId }, paysol);
      if (data.success) this.sourceStock = data.data.filter(r => r.on_hand > 0);
    },
    onVarSearch() {
      clearTimeout(varTimer);
      const q = this.varSearch.trim().toLowerCase();
      if (q.length < 2) { this.varResults = []; return; }
      varTimer = setTimeout(() => {
        this.varResults = this.sourceStock.filter(r =>
          (r.product_name || '').toLowerCase().includes(q) ||
          (r.variant_name || '').toLowerCase().includes(q) ||
          (r.sku || '').toLowerCase().includes(q)
        ).slice(0, 10);
      }, 200);
    },
    addLine(v) {
      if (this.form.items.some(i => i.variantId === v.variant_id)) return;
      this.form.items.push({
        variantId: v.variant_id, product_name: v.product_name, variant_name: v.variant_name,
        sku: v.sku, on_hand: v.on_hand, sentQty: 1
      });
      this.varSearch = ''; this.varResults = [];
    },
    async save() {
      this.saving = true; this.err = '';
      try {
        const payload = {
          function: 'saveStockTransfer', id: this.form.id, fromShopId: this.form.fromShopId, toShopId: this.form.toShopId,
          notes: this.form.notes,
          items: this.form.items.map(it => ({ variantId: it.variantId, sentQty: it.sentQty })),
        };
        const { data } = await execute(payload, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showForm = false; await this.load();
      } catch (e) { this.err = e.response?.data?.message || 'Save failed.'; }
      finally { this.saving = false; }
    },
    async openDetail(id) {
      const { data } = await execute({ function: 'getStockTransfer', id }, paysol);
      if (data.success) {
        this.detail = data.data; this.detailErr = ''; this.detailOk = '';
        this.receipts = {};
        if (this.detail.status === 'in_transit') this.detail.items.forEach(it => this.receipts[it.id] = it.sent_qty);
      }
    },
    async sendTransfer() {
      const { data } = await execute({ function: 'sendStockTransfer', id: this.detail.id }, paysol);
      if (data.success) { await this.load(); await this.openDetail(this.detail.id); }
      else this.detailErr = data.message;
    },
    async cancelTransfer() {
      if (!confirm('Cancel this draft transfer?')) return;
      const { data } = await execute({ function: 'cancelStockTransfer', id: this.detail.id }, paysol);
      if (data.success) { this.detail = null; await this.load(); }
    },
    async receiveTransfer() {
      this.receiving = true; this.detailErr = ''; this.detailOk = '';
      try {
        const items = Object.entries(this.receipts).map(([id, q]) => ({ transferItemId: parseInt(id), receivedQty: q }));
        const { data } = await execute({ function: 'receiveStockTransfer', id: this.detail.id, items }, paysol);
        if (!data.success) { this.detailErr = data.message; return; }
        this.detailOk = data.message; await this.load();
        setTimeout(() => { this.openDetail(this.detail.id); }, 500);
      } finally { this.receiving = false; }
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1300px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }
.clickable { cursor: pointer; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
button.small { padding: 4px 10px; font-size: 11px; }
.st-pill { display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.st-pill.draft      { background: rgba(139,164,196,.15); color: var(--paysol-muted); }
.st-pill.in_transit { background: rgba(79,195,247,.15); color: #4FC3F7; }
.st-pill.received   { background: rgba(16,185,129,.15); color: var(--paysol-success); }
.st-pill.cancelled  { background: rgba(239,68,68,.15); color: var(--paysol-danger); }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 500px; max-height: 90vh; overflow-y: auto; }
.modal.wide { max-width: 800px; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.var-results { max-height: 220px; overflow-y: auto; background: var(--paysol-bg-elev); border: 1px solid var(--paysol-line); border-radius: 6px; margin-top: 4px; }
.var-result { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--paysol-line); }
.var-result:hover { background: var(--paysol-teal-glow); }
.var-result:last-child { border-bottom: none; }
.items-mini { margin: 10px 0; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
.ok  { color: var(--paysol-success); font-size: 12px; margin-top: 8px; }
.detail-head { display: flex; justify-content: space-between; align-items: start; margin-bottom: 16px; }
.detail-head h3 { margin: 0 0 4px; color: var(--paysol-teal); }
.detail-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
</style>
